'use client';

import type { ReactNode } from 'react';
import { isTRPCClientError, updateWithToast } from '@/clientOnlyUtils/clientUtils';
import { reportSoftphoneDiagnostic } from '@/clientOnlyUtils/reportSoftphoneDiagnostic';
import { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import toast from 'react-hot-toast';

import type { RouterInputs } from '@poseidon/api/types';
import type { CallOutcomeExtra } from '@poseidon/components/Softphone/CallOutcomePicker';
import type {
  SoftphoneClient,
  SoftphoneProvider as SoftphoneProviderName,
} from '@poseidon/components/Softphone/softphoneClient.types';
import { carrierUsesOurCallerNumber } from '@poseidon/components/Softphone/callingSetup';
import {
  CALL_OUTCOMES,
  isPickerCallOutcome,
  WRONG_NUMBER_MARKED_TOAST,
} from '@poseidon/components/Softphone/CallOutcomePicker';
import { createSoftphoneClient } from '@poseidon/components/Softphone/createSoftphoneClient';
import { JustCallDock } from '@poseidon/components/Softphone/JustCallDock';
import { SoftphoneDialerPure } from '@poseidon/components/Softphone/SoftphoneDialerPure';
import { useSoftphone } from '@poseidon/components/Softphone/useSoftphone';
import { useVoicemailDrop, voicemailDropAllowedCallId } from '@poseidon/components/Softphone/useVoicemailDrop';

import { useInvalidateCadenceState } from '@/hooks/trpc/useCadenceMutation';
import { api } from '@/utils/api';

type CallOutcome = RouterInputs['voiceCall']['logOutcome']['outcome'];

function isCallOutcome(value: string): value is CallOutcome {
  // Picker grid values + confirmed auto outcomes + human follow-ups — all accepted by logOutcome.
  return isPickerCallOutcome(value);
}

/** Everything the global dialer needs to place + log a call for one contact. */
export interface SoftphoneCallTarget {
  teamId: string;
  /** Public contact id (contact_public_id). */
  contactId: string;
  contactName?: string;
  phoneNumber: string;
  /**
   * The dialed number is the contact's org ("firm") number (person has no direct number). Surfaces
   * a "Firm number" badge on the dialer.
   */
  isFirmNumber?: boolean;
  /** Contact photo for the dialer avatar; initials fallback when absent. */
  imageSrc?: string | null;
  /** The team's carrier; picks the matching softphone client (Telnyx / Twilio / JustCall). */
  provider: SoftphoneProviderName;
  /**
   * When placing the call for a cadence `call` step, the cadence_step id. Threaded to
   * `startCall` so logging the outcome completes that step server-side.
   */
  cadenceStepId?: string;
  /**
   * When placing the call for a standalone scheduled `call` reminder (no cadence step), the
   * global_reminder id. Threaded to `logOutcome` so logging the outcome clears the reminder.
   */
  globalReminderId?: string;
  /** Called after a call outcome is logged (e.g. to invalidate the originating page's caches). */
  onCallLogged?: () => void;
}

/** A placed call: the target plus a monotonic id so re-calling the SAME contact still re-triggers. */
interface ActiveCall {
  id: number;
  target: SoftphoneCallTarget;
}

interface SoftphoneContextValue {
  /** Summon the global dialer for a contact and start dialing. */
  startCall: (target: SoftphoneCallTarget) => void;
  /** A call session is active (dialing, connected, or on the outcome screen). */
  isBusy: boolean;
}

const SoftphoneContext = createContext<SoftphoneContextValue | null>(null);

/**
 * Access the app-global dialer. Throws outside `SoftphoneProvider` so a missing mount is a loud
 * dev error rather than a silently dead Call button.
 */
export function useSoftphoneLauncher(): SoftphoneContextValue {
  const ctx = useContext(SoftphoneContext);
  if (!ctx) throw new Error('useSoftphoneLauncher must be used within a SoftphoneProvider');
  return ctx;
}

/**
 * App-global softphone. Mounted once at the root (`_app.tsx`) so the dialer is summoned from any
 * contact's Call button yet lives above the whole app — a live call keeps ringing/connected while
 * the rep navigates to a different page or tab, instead of being torn down when the contact page
 * unmounts. Only one call at a time: while a call is active, `isBusy` greys every other Call button.
 *
 * The `SoftphoneEngine` is mounted lazily on the first call (once the carrier is known) and then
 * kept alive for the app's lifetime, so its ONE softphone client is reused across calls — the
 * Twilio Device stays registered instead of being destroyed + re-registered per call, which is
 * exactly what corrupts consecutive calls (see `twilioSoftphoneClient.init`).
 */
export function SoftphoneProvider({ children }: { children: ReactNode }) {
  // The carrier is fixed per user/team; capture it from the first call to mount the engine.
  const [carrier, setCarrier] = useState<SoftphoneProviderName | null>(null);
  const [activeCall, setActiveCall] = useState<ActiveCall | null>(null);
  const nextIdRef = useRef(1);

  const startCall = useCallback((target: SoftphoneCallTarget) => {
    setCarrier((c) => c ?? target.provider);
    setActiveCall({ id: nextIdRef.current++, target });
  }, []);
  const endCall = useCallback(() => setActiveCall(null), []);

  const value = useMemo<SoftphoneContextValue>(
    () => ({ startCall, isBusy: activeCall !== null }),
    [startCall, activeCall],
  );

  return (
    <SoftphoneContext.Provider value={value}>
      {children}
      {carrier && <SoftphoneEngine carrier={carrier} activeCall={activeCall} onEnd={endCall} />}
      {/* JustCall's dialer, page-wide: renders nothing until a JustCall call (here or on the calls
          page) or the settings page asks for it, then stays put so its iframe never reloads. */}
      <JustCallDock />
    </SoftphoneContext.Provider>
  );
}

/**
 * The persistent call engine. Owns the ONE softphone client + `useSoftphone` controller for the
 * app's lifetime (reused across calls so the Device stays registered), starts a call whenever a
 * new one is placed, and renders the shared `SoftphoneDialerPure` while a call is active. Only the
 * `activeCall` toggles — the client is never rebuilt between calls, and never on navigation.
 */
function SoftphoneEngine({
  carrier,
  activeCall,
  onEnd,
}: {
  carrier: SoftphoneProviderName;
  activeCall: ActiveCall | null;
  onEnd: () => void;
}) {
  const [changeNumberOpen, setChangeNumberOpen] = useState(false);

  const utils = api.useUtils();
  const invalidateCadenceState = useInvalidateCadenceState();
  const getTokenMutation = api.voiceCall.getClientToken.useMutation();
  const startCallMutation = api.voiceCall.startCall.useMutation();
  const logOutcomeMutation = api.voiceCall.logOutcome.useMutation();
  const setDialNumberMutation = api.voiceCall.setContactDialNumber.useMutation();
  const clearDialNumberMutation = api.voiceCall.clearContactDialNumber.useMutation();
  const reportProviderCallMutation = api.voiceCall.reportProviderCall.useMutation();
  const dropVoicemailMutation = api.voiceCall.dropVoicemail.useMutation();
  // Which call the server said may take a voicemail drop — set per call from startCall's answer.
  const [voicemailDropCallId, setVoicemailDropCallId] = useState<string | null>(null);

  const target = activeCall?.target ?? null;
  // Latest target via ref so the stable start closures always read the current contact.
  const targetRef = useRef<SoftphoneCallTarget | null>(target);
  targetRef.current = target;
  // The team of the last call, kept after the dialer closes: the Device stays registered between
  // calls and asks for a fresh token when its hour is up, with no call in progress to read it from.
  const lastTeamIdRef = useRef<string | null>(null);
  if (target != null) lastTeamIdRef.current = target.teamId;

  // One client for the engine's whole life — reused across calls (see the component docblock).
  const clientRef = useRef<SoftphoneClient | null>(null);
  if (clientRef.current === null) clientRef.current = createSoftphoneClient(carrier);
  const client = clientRef.current;

  const fetchToken = useCallback(async () => {
    const teamId = targetRef.current?.teamId ?? lastTeamIdRef.current;
    if (!teamId) throw new Error('No active call');
    const res = await getTokenMutation.mutateAsync({ teamId });
    return { token: res.token };
  }, [getTokenMutation]);

  const startServerCall = useCallback(async () => {
    const t = targetRef.current;
    if (!t) throw new Error('No active call');
    // Refresh the dial info whichever way this goes: a first dial pins the contact's from-number
    // server-side (the change-number flow must see it), and a refusal for today's call limits must
    // grey out the Call button behind the dialer rather than leave a stale "callable" answer.
    const res = await startCallMutation
      .mutateAsync({
        teamId: t.teamId,
        contactId: t.contactId,
        stepId: t.cadenceStepId,
      })
      .finally(() => void utils.voiceCall.getContactDialInfo.invalidate({ teamId: t.teamId, contactId: t.contactId }));
    setVoicemailDropCallId(voicemailDropAllowedCallId(res));
    return {
      voiceCallId: res.voiceCallId,
      destinationNumber: res.destinationNumber,
      callerNumber: res.callerNumber,
      recordingEnabled: res.recordingEnabled,
    };
  }, [startCallMutation, utils]);

  // How the carrier settled the call — lets the dialer show "Twilio blocked this call" instead of
  // the outcome picker. Uses the vanilla client (a plain request per poll attempt) rather than the
  // query cache: nothing ever reads these entries back, so caching them would only leak.
  const fetchCallResult = useCallback(
    async (callId: string) => {
      const teamId = targetRef.current?.teamId;
      if (!teamId) throw new Error('No active call');
      return utils.client.voiceCall.getCallResult.query({ teamId, voiceCallId: callId });
    },
    [utils],
  );

  // JustCall only: hand JustCall's call id to the server so it can pull the call's result.
  const reportProviderCall = useCallback(
    async (params: { voiceCallId: string; providerCallId: string; ended: boolean }) => {
      const teamId = targetRef.current?.teamId;
      if (!teamId) return;
      await reportProviderCallMutation.mutateAsync({ teamId, ...params });
    },
    [reportProviderCallMutation],
  );

  const controller = useSoftphone({
    client,
    fetchToken,
    startServerCall,
    fetchCallResult,
    onDiagnostic: reportSoftphoneDiagnostic,
    reportProviderCall,
  });
  const { voiceCallId, start, close: closeController } = controller;

  // Start dialing whenever a NEW call is placed (activeCall.id changes) — including a repeat call to
  // the same contact. The id guard keeps a changing `start` identity from re-dialing the same call.
  const startedIdRef = useRef<number | null>(null);
  useEffect(() => {
    if (!activeCall) return;
    if (startedIdRef.current === activeCall.id) return;
    startedIdRef.current = activeCall.id;
    void start();
  }, [activeCall, start]);

  // The contact's pinned from-number + the rep's bucket, for the dialer's "Calling from" line and
  // the guarded change-number flow. Follows the active contact; idle between calls. Skipped for
  // JustCall, which picks the from-number in its own dialer.
  const dialInfoQuery = api.voiceCall.getContactDialInfo.useQuery(
    { teamId: target?.teamId ?? '', contactId: target?.contactId ?? '' },
    { enabled: target !== null && carrierUsesOurCallerNumber(carrier) },
  );

  const handleClose = useCallback(() => {
    closeController();
    setChangeNumberOpen(false);
    onEnd();
  }, [closeController, onEnd]);

  const handleConfirmChangeNumber = useCallback(
    async (newNumber: string) => {
      const t = targetRef.current;
      if (!t) return;
      await updateWithToast({
        loading: 'Updating calling number…',
        success: 'Calling number updated',
        mutationFnAsync: () =>
          setDialNumberMutation.mutateAsync({ teamId: t.teamId, contactId: t.contactId, phoneNumber: newNumber }),
        // Only on success (updateWithToast swallows the error): a rejected re-pin — the number was
        // unassigned from the rep a moment ago, say — must leave the popup open rather than closing
        // as though it took.
        onAfterSuccess: () => {
          void utils.voiceCall.getContactDialInfo.invalidate({ teamId: t.teamId, contactId: t.contactId });
          setChangeNumberOpen(false);
        },
      });
    },
    [setDialNumberMutation, utils],
  );

  const handleClearChangeNumber = useCallback(async () => {
    const t = targetRef.current;
    if (!t) return;
    await updateWithToast({
      loading: 'Clearing calling number…',
      success: 'Calls to this contact will rotate again',
      mutationFnAsync: () => clearDialNumberMutation.mutateAsync({ teamId: t.teamId, contactId: t.contactId }),
      // Only on success (updateWithToast swallows the error): a failed clear leaves the popup open
      // so the rep can retry rather than believing the pin is gone.
      onAfterSuccess: () => {
        void utils.voiceCall.getContactDialInfo.invalidate({ teamId: t.teamId, contactId: t.contactId });
        setChangeNumberOpen(false);
      },
    });
  }, [clearDialNumberMutation, utils]);

  const handleSelectOutcome = useCallback(
    async (outcome: string, extra?: CallOutcomeExtra) => {
      const t = targetRef.current;
      if (t && voiceCallId && isCallOutcome(outcome)) {
        await updateWithToast({
          loading: 'Saving call outcome…',
          // A "wrong number" outcome also removes the dialed number from the contact server-side.
          success: outcome === 'wrong_number' ? WRONG_NUMBER_MARKED_TOAST : 'Call logged',
          mutationFnAsync: () =>
            logOutcomeMutation.mutateAsync({
              teamId: t.teamId,
              voiceCallId,
              outcome,
              notes: extra?.notes,
              callbackAt: extra?.callbackAt,
              // A standalone scheduled call → clear its reminder once logged.
              globalReminderId: t.globalReminderId,
              // Confirming the automatic disposition keeps it automatic (see CallOutcomePicker).
              source: extra?.source,
            }),
        });
        // Refresh the contact's conversation thread so the just-logged call (and its disposition)
        // appears without a manual reload, regardless of which page the rep is on now.
        void utils.history.contact.invalidate({ contactId: t.contactId });
        // A campaign call finishes its step (and maybe the campaign) and deletes the step's
        // reminder, so it refreshes every campaign view, which includes the contact and the reminder
        // views. Otherwise refresh just the contact: logOutcome can append a note ('other') or
        // remove a wrong number server-side, and both live on the contact record.
        const isCampaignCall = t.cadenceStepId != null;
        if (isCampaignCall) void invalidateCadenceState();
        else void utils.contact.byId.invalidate({ contactId: t.contactId });
        // Every outcome stamps last_dialed_at (tried/untried tabs), and a
        // wrong-number mark both condemns the row and starts a background
        // search — the Bad Numbers page needs fresh data to show the row and
        // begin polling for the search's completion.
        void utils.phoneReEnrichment.badNumberContacts.invalidate();
        // A non-connecting outcome advances the contact's from-number server-side. The cached
        // dial info is what the dialer shows before the next call starts, so without this the rep
        // reopens it and sees the number the call just rotated away from.
        void utils.voiceCall.getContactDialInfo.invalidate({ teamId: t.teamId, contactId: t.contactId });
        // A "call back" outcome created a reminder; a standalone scheduled call was cleared —
        // either way refresh the contact's reminder views (unless the campaign refresh above already
        // did) + the calls queue.
        if (outcome === 'callback' || t.globalReminderId != null) {
          if (!isCampaignCall) {
            void utils.globalReminder.invalidate();
            void utils.contactDetailBootstrap.forContact.invalidate();
          }
          void utils.voiceCall.callQueue.invalidate();
          void utils.voiceCall.callQueuePage.invalidate();
        }
        t.onCallLogged?.();
      }
      // Hang up any still-live call and reset the controller before hiding the dialer.
      closeController();
      setChangeNumberOpen(false);
      onEnd();
    },
    [voiceCallId, closeController, logOutcomeMutation, utils, invalidateCadenceState, onEnd],
  );

  const dropVoicemail = useCallback(
    async (callId: string) => {
      const teamId = targetRef.current?.teamId;
      if (!teamId) throw new Error('No active call');
      try {
        return await dropVoicemailMutation.mutateAsync({ teamId, voiceCallId: callId });
      } catch (e) {
        // The button shows its own inline "still live" error; the toast carries the server's reason.
        toast.error(isTRPCClientError(e) ? e.message : 'Couldn’t leave the voicemail');
        throw e;
      }
    },
    [dropVoicemailMutation],
  );

  // "Leave voicemail": the prospect's leg now plays the rep's message, so release ours and move on
  // exactly like a logged call — the disposition is already Left voicemail (automatic), confirmed.
  const voicemailDrop = useVoicemailDrop({
    voiceCallId: controller.voiceCallId,
    status: controller.status,
    allowedVoiceCallId: voicemailDropCallId,
    drop: dropVoicemail,
    onDropped: () => {
      controller.hangup();
      void handleSelectOutcome('left_voicemail', { source: 'auto' });
    },
  });

  // No active call → keep the engine (and its reused client) mounted, but render no dialer.
  if (!target) return null;

  const bucketNumbers = dialInfoQuery.data?.bucketNumbers ?? [];
  // The in-flight call's number is authoritative (a first dial pins it server-side); before a
  // call starts, fall back to the already-pinned number from the query.
  // Before startCall answers: a pin at today's limit lends the call to its stand-in, so name that.
  const callerNumber =
    controller.callerNumber ?? dialInfoQuery.data?.standInNumber ?? dialInfoQuery.data?.stickyNumber ?? null;
  // An empty bucket means a legacy/superadmin-provisioned rep, not a stale association.
  const callerNumberStale = callerNumber != null && bucketNumbers.length > 0 && !bucketNumbers.includes(callerNumber);

  return (
    <SoftphoneDialerPure
      contactName={target.contactName}
      phoneNumber={target.phoneNumber}
      isFirmNumber={target.isFirmNumber}
      imageSrc={target.imageSrc}
      status={controller.status}
      durationSec={controller.durationSec}
      isMuted={controller.isMuted}
      error={controller.error}
      carrierFailure={controller.carrierFailure}
      autoOutcome={controller.autoOutcome}
      isRecording={controller.isRecording}
      embeddedCarrierDialer={controller.embeddedCarrierDialer}
      notice={controller.notice}
      outcomes={CALL_OUTCOMES}
      onToggleMute={controller.toggleMute}
      onHangup={controller.hangup}
      onSendDigits={controller.sendDigits}
      voicemailDrop={voicemailDrop}
      onSelectOutcome={(outcome, extra) => void handleSelectOutcome(outcome, extra)}
      onClose={handleClose}
      callerNumber={callerNumber}
      callerNumberStale={callerNumberStale}
      bucketNumbers={bucketNumbers}
      callerNumberLocked={dialInfoQuery.data?.rotationLocked}
      callerNumberHistory={dialInfoQuery.data?.history}
      callerNumberLimits={dialInfoQuery.data?.numberLimits}
      changeNumberOpen={changeNumberOpen}
      changeNumberSaving={setDialNumberMutation.isPending}
      changeNumberClearing={clearDialNumberMutation.isPending}
      onOpenChangeNumber={() => setChangeNumberOpen(true)}
      onCloseChangeNumber={() => setChangeNumberOpen(false)}
      onConfirmChangeNumber={(newNumber) => void handleConfirmChangeNumber(newNumber)}
      onClearChangeNumber={() => void handleClearChangeNumber()}
    />
  );
}
